import { useCallback, useRef } from 'react';
function getShapeBounds(shape) {
    switch (shape.type) {
        case 'box':
            return { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
        case 'circle':
            return {
                x: shape.cx - shape.rx,
                y: shape.cy - shape.ry,
                width: shape.rx * 2,
                height: shape.ry * 2,
            };
        case 'line':
        case 'arrow':
            return {
                x: Math.min(shape.x1, shape.x2),
                y: Math.min(shape.y1, shape.y2),
                width: Math.abs(shape.x2 - shape.x1),
                height: Math.abs(shape.y2 - shape.y1),
            };
        case 'freeform': {
            if (shape.points.length === 0)
                return { x: 0, y: 0, width: 0, height: 0 };
            let minX = shape.points[0].x, minY = shape.points[0].y;
            let maxX = minX, maxY = minY;
            for (const p of shape.points) {
                if (p.x < minX)
                    minX = p.x;
                if (p.y < minY)
                    minY = p.y;
                if (p.x > maxX)
                    maxX = p.x;
                if (p.y > maxY)
                    maxY = p.y;
            }
            return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
        }
        case 'text':
            return { x: shape.x, y: shape.y, width: shape.width, height: shape.height };
    }
}
function resizeShapeToBounds(shape, originalBounds, newBounds, handle) {
    const scaleX = originalBounds.width !== 0 ? newBounds.width / originalBounds.width : 1;
    const scaleY = originalBounds.height !== 0 ? newBounds.height / originalBounds.height : 1;
    switch (shape.type) {
        case 'box':
            return { ...shape, x: newBounds.x, y: newBounds.y, width: newBounds.width, height: newBounds.height };
        case 'circle':
            return {
                ...shape,
                cx: newBounds.x + newBounds.width / 2,
                cy: newBounds.y + newBounds.height / 2,
                rx: newBounds.width / 2,
                ry: newBounds.height / 2,
            };
        case 'line':
        case 'arrow': {
            const x1Rel = (shape.x1 - originalBounds.x) * scaleX + newBounds.x;
            const y1Rel = (shape.y1 - originalBounds.y) * scaleY + newBounds.y;
            const x2Rel = (shape.x2 - originalBounds.x) * scaleX + newBounds.x;
            const y2Rel = (shape.y2 - originalBounds.y) * scaleY + newBounds.y;
            return { ...shape, x1: x1Rel, y1: y1Rel, x2: x2Rel, y2: y2Rel };
        }
        case 'freeform':
            return {
                ...shape,
                points: shape.points.map((p) => ({
                    x: (p.x - originalBounds.x) * scaleX + newBounds.x,
                    y: (p.y - originalBounds.y) * scaleY + newBounds.y,
                })),
            };
        case 'text': {
            // Corner handles scale font proportionally; edge handles only resize
            // the bounding box so the user can adjust the frame without changing
            // the text size.  Enforce a minimum font size of 6px.
            const isCorner = handle === 'nw' || handle === 'ne' || handle === 'se' || handle === 'sw';
            const scale = isCorner ? Math.min(scaleX, scaleY) : 1;
            return {
                ...shape,
                x: newBounds.x,
                y: newBounds.y,
                width: newBounds.width,
                height: newBounds.height,
                fontSize: Math.max(shape.fontSize * scale, 6),
            };
        }
    }
}
export function useShapeResize({ shapes, dispatch, containerWidth, containerHeight }) {
    const resizeState = useRef(null);
    const rafId = useRef(0);
    const pendingResize = useRef(null);
    const startResize = useCallback((shapeId, handle, clientX, clientY) => {
        const shape = shapes.find((s) => s.id === shapeId);
        if (!shape)
            return;
        resizeState.current = {
            shapeId,
            handle,
            startX: clientX,
            startY: clientY,
            originalShape: shape,
        };
    }, [shapes]);
    const flushPendingResize = useCallback(() => {
        if (pendingResize.current) {
            dispatch({ type: 'RESIZE_SHAPE', id: pendingResize.current.id, shape: pendingResize.current.shape });
            pendingResize.current = null;
        }
        rafId.current = 0;
    }, [dispatch]);
    const onResizeMove = useCallback((clientX, clientY, shiftKey) => {
        const state = resizeState.current;
        if (!state || containerWidth === 0 || containerHeight === 0)
            return;
        let dxNorm = (clientX - state.startX) / containerWidth;
        let dyNorm = (clientY - state.startY) / containerHeight;
        // If shape is rotated, project the screen-space delta into the shape's local axes
        const rotation = state.originalShape.rotation ?? 0;
        if (rotation !== 0) {
            const rad = (-rotation * Math.PI) / 180;
            const cos = Math.cos(rad);
            const sin = Math.sin(rad);
            // Convert to screen pixels, rotate, convert back to normalized
            const dxPx = dxNorm * containerWidth;
            const dyPx = dyNorm * containerHeight;
            dxNorm = (dxPx * cos - dyPx * sin) / containerWidth;
            dyNorm = (dxPx * sin + dyPx * cos) / containerHeight;
        }
        const originalBounds = getShapeBounds(state.originalShape);
        let { x, y, width, height } = originalBounds;
        // Apply delta based on handle position
        const handle = state.handle;
        if (handle.includes('w')) {
            x += dxNorm;
            width -= dxNorm;
        }
        if (handle.includes('e')) {
            width += dxNorm;
        }
        if (handle === 'n' || handle === 'nw' || handle === 'ne') {
            y += dyNorm;
            height -= dyNorm;
        }
        if (handle === 's' || handle === 'sw' || handle === 'se') {
            height += dyNorm;
        }
        // Enforce minimum size (larger minimum for text shapes to stay readable)
        const minSize = state.originalShape.type === 'text' ? 0.03 : 0.005;
        if (width < minSize)
            width = minSize;
        if (height < minSize)
            height = minSize;
        // Shift-constrain aspect ratio
        if (shiftKey && originalBounds.width > 0 && originalBounds.height > 0) {
            const ratio = originalBounds.width / originalBounds.height;
            const isEdgeH = handle === 'e' || handle === 'w';
            const isEdgeV = handle === 'n' || handle === 's';
            if (isEdgeH) {
                // Only width changed — grow height proportionally, centered
                height = width / ratio;
                y = originalBounds.y + (originalBounds.height - height) / 2;
            }
            else if (isEdgeV) {
                // Only height changed — grow width proportionally, centered
                width = height * ratio;
                x = originalBounds.x + (originalBounds.width - width) / 2;
            }
            else {
                // Corner handle — constrain to original aspect ratio
                if (width / height > ratio) {
                    width = height * ratio;
                }
                else {
                    height = width / ratio;
                }
            }
        }
        const newBounds = { x, y, width, height };
        const resized = resizeShapeToBounds(state.originalShape, originalBounds, newBounds, handle);
        // Throttle to animation frames to avoid excessive re-renders (especially with large text)
        pendingResize.current = { id: state.shapeId, shape: resized };
        if (!rafId.current) {
            rafId.current = requestAnimationFrame(flushPendingResize);
        }
    }, [containerWidth, containerHeight, flushPendingResize]);
    const endResize = useCallback(() => {
        // Flush any pending rAF resize before ending
        if (rafId.current) {
            cancelAnimationFrame(rafId.current);
            rafId.current = 0;
            if (pendingResize.current) {
                dispatch({ type: 'RESIZE_SHAPE', id: pendingResize.current.id, shape: pendingResize.current.shape });
                pendingResize.current = null;
            }
        }
        const info = resizeState.current;
        resizeState.current = null;
        return info ? { shapeId: info.shapeId, handle: info.handle } : null;
    }, [dispatch]);
    const isResizing = useCallback(() => resizeState.current !== null, []);
    return { startResize, onResizeMove, endResize, isResizing };
}
export { getShapeBounds };
